package fr.frux.core.network.auth

import fr.frux.core.common.auth.SessionExpiryNotifier
import fr.frux.core.common.auth.TokenStorage
import fr.frux.core.common.auth.Tokens
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.AuthApi
import io.ktor.client.plugins.ClientRequestException
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/** Résultat d'une tentative de rotation du refresh token (#26, ADR 0019). */
internal sealed interface RefreshOutcome {
    /** Nouvelle paire posée en stockage, prête à re-poser sur la requête. */
    data class Refreshed(
        val tokens: Tokens,
    ) : RefreshOutcome

    /**
     * Refus définitif du serveur (401/400 `INVALID_REFRESH_TOKEN`) ou aucune paire à rafraîchir :
     * le stockage a été purgé et [SessionExpiryNotifier] déjà notifié une fois.
     */
    data object SessionDead : RefreshOutcome

    /** Réseau, 5xx ou 429 : la paire est conservée intacte, l'erreur remonte à l'appelant. */
    data class TransientFailure(
        val cause: Throwable,
    ) : RefreshOutcome
}

/**
 * Rotation du refresh token, partagée par le proactif ([AuthInterceptor]) et le réactif
 * ([FruxTokenRefresh]) d'un même `HttpClient` (#26, ADR 0019).
 *
 * `Mutex.withLock` sur l'intégralité de [refresh] plutôt qu'un flag séparé : ça sérialise tout
 * seul les appels concurrents (proactifs comme réactifs) sur un unique appel réseau, et le
 * *stale check* (relire le stockage une fois le verrou pris) évite à un appelant qui attendait le
 * verrou de rejouer un appel devenu inutile — que le refresh gagnant ait réussi (nouvelle paire
 * déjà posée) ou ait tué la session (stockage déjà vidé, pas de deuxième notification).
 *
 * [refreshApi] tourne sur un client Ktor minimal dédié (cf. `createFruxHttpClient`), jamais le
 * client principal : y faire transiter `/auth/refresh` recréerait la récursion que ce plugin
 * cherche justement à éviter.
 */
class TokenRefresher(
    private val tokenStorage: TokenStorage,
    private val sessionExpiryNotifier: SessionExpiryNotifier,
    private val refreshApi: AuthApi,
    private val now: () -> Long = { epochSecondsNow() },
) {
    private val mutex = Mutex()

    /** Access qui expire dans moins de 60 s : évite le 401 garanti au réveil de l'app (ADR 0019). */
    suspend fun refreshIfExpiringSoon(tokens: Tokens): Tokens? {
        if (tokens.accessExpiresAtEpochSeconds - now() >= PROACTIVE_WINDOW_SECONDS) return tokens
        return when (val outcome = refresh(tokens.accessToken)) {
            is RefreshOutcome.Refreshed -> outcome.tokens

            RefreshOutcome.SessionDead -> null

            // Panne transitoire du refresh proactif : l'access encore valide quelques secondes
            // reste la meilleure option, le réactif prendra le relais s'il finit par expirer.
            is RefreshOutcome.TransientFailure -> tokens
        }
    }

    /**
     * [staleAccessToken] est l'access que l'appelant a vu périmé ou refusé (401) : sert au stale
     * check ci-dessus. `null` pour un premier appel sans référence (jamais le cas ici en pratique,
     * gardé pour la symétrie des deux appelants).
     */
    internal suspend fun refresh(staleAccessToken: String?): RefreshOutcome =
        mutex.withLock {
            val current = tokenStorage.load()
            if (current == null) {
                // Rien à rafraîchir : déjà purgé par un refresh concurrent qui a gagné la course,
                // ou jamais authentifié. Ne pas re-notifier : ce n'est pas un nouvel incident.
                return@withLock RefreshOutcome.SessionDead
            }
            if (staleAccessToken != null && current.accessToken != staleAccessToken) {
                // Un refresh concurrent a mis à jour le stockage pendant l'attente du verrou.
                return@withLock RefreshOutcome.Refreshed(current)
            }
            performNetworkRefresh(current)
        }

    // runCatchingCancellable plutôt qu'un `catch (e: Exception)` local : la panne réseau attendue
    // ici couvre un spectre large et non listable à l'avance (timeout, DNS, JsError sur wasmJs/js
    // qui dérive de Throwable et non d'Exception, cf. #121) — c'est le rôle documenté de ce
    // helper, pas une généricité de complaisance.
    private suspend fun performNetworkRefresh(current: Tokens): RefreshOutcome =
        runCatchingCancellable {
            val response = refreshApi.refreshToken(current.refreshToken)
            Tokens(
                accessToken = response.accessToken,
                refreshToken = response.refreshToken,
                accessExpiresAtEpochSeconds = now() + response.expiresIn,
            )
        }.fold(
            onSuccess = { fresh ->
                tokenStorage.save(fresh)
                RefreshOutcome.Refreshed(fresh)
            },
            onFailure = { error ->
                val invalidRefreshToken =
                    error is ClientRequestException &&
                        (
                            error.response.status == HttpStatusCode.Unauthorized ||
                                error.response.status == HttpStatusCode.BadRequest
                        )
                if (invalidRefreshToken) {
                    tokenStorage.clear()
                    sessionExpiryNotifier.notifyExpired()
                    RefreshOutcome.SessionDead
                } else {
                    RefreshOutcome.TransientFailure(error)
                }
            },
        )

    private companion object {
        const val PROACTIVE_WINDOW_SECONDS = 60L
    }
}

@OptIn(ExperimentalTime::class)
private fun epochSecondsNow(): Long = Clock.System.now().epochSeconds
